import { Children, useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { Link } from "react-router-dom";

import tagDot from "../../assets/Images/Temples/tag-dot.svg";
import dividerLines from "../../assets/Images/Temples/divider-lines.svg";
import dividerLotus from "../../assets/Images/Temples/divider-lotus.svg";

export const TEMPLE_COLORS = {
  red: "#F04951",
  heroRed: "#EC1B26",
  ink: "#1D1D1B",
  body: "#6B6560",
  muted: "#7D7D7D",
  gold: "#C9952B",
  blush: "#FDEAEB",
};

export const HINDI_FONT = "'Yatra One', 'Red Hat Display', serif";

// "• WHY TEMPLES MATTER" label used above most section headings.
export const SectionTag = ({ children, small, sx }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: small ? "10px" : "12px", ...sx }}>
    <Box component="img" src={tagDot} alt="" sx={{ width: 8, height: 8 }} />
    <Typography
      sx={{
        fontWeight: 500,
        fontSize: small ? "12px" : "14px",
        lineHeight: "16px",
        letterSpacing: "2px",
        textTransform: "uppercase",
        color: TEMPLE_COLORS.ink,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </Typography>
  </Box>
);

// Thin red rule with a lotus in the middle, under the centred headings.
export const LotusDivider = ({ small }) =>
  small ? (
    <Box sx={{ position: "relative", width: 140, height: 16, flexShrink: 0 }}>
      <Box component="img" src={dividerLines} alt="" sx={{ position: "absolute", left: 0, top: "1.58px", width: 140, height: 12.83 }} />
      <Box component="img" src={dividerLotus} alt="" sx={{ position: "absolute", left: "54.05px", top: "6.06px", width: 31.9, height: 3.89 }} />
    </Box>
  ) : (
    <Box sx={{ position: "relative", width: 180, height: 18, flexShrink: 0 }}>
      <Box
        component="img"
        src={dividerLines}
        alt=""
        sx={{ position: "absolute", left: 0, top: "0.75px", width: 180, height: 16.5 }}
      />
      <Box
        component="img"
        src={dividerLotus}
        alt=""
        sx={{ position: "absolute", left: "69.5px", top: "6.5px", width: 41, height: 5 }}
      />
    </Box>
  );

export const SectionHeading = ({ tag, title, description, isMobile }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: isMobile ? "12px" : "16px",
      textAlign: "center",
      maxWidth: 820,
      mx: "auto",
    }}
  >
    {tag && <SectionTag small={isMobile}>{tag}</SectionTag>}
    <Typography
      component="h2"
      sx={{
        fontWeight: 700,
        fontSize: isMobile ? "28px" : "48px",
        lineHeight: isMobile ? "36px" : "58px",
        color: TEMPLE_COLORS.ink,
      }}
    >
      {title}
    </Typography>
    <LotusDivider small={isMobile} />
    {description && (
      <Typography
        sx={{
          fontSize: isMobile ? "15px" : "17px",
          lineHeight: isMobile ? "24px" : "28px",
          color: isMobile ? TEMPLE_COLORS.body : TEMPLE_COLORS.muted,
          maxWidth: 760,
        }}
      >
        {description}
      </Typography>
    )}
  </Box>
);

// Hero/CTA button: red fill with a soft red glow, or white with a grey outline.
const StyledTempleButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "outlined",
})(({ outlined }) => ({
  fontFamily: "'Red Hat Display', sans-serif",
  fontWeight: 700,
  fontSize: "16px",
  lineHeight: "20px",
  textTransform: "none",
  borderRadius: "10px",
  padding: "16px 30px",
  whiteSpace: "nowrap",
  color: outlined ? TEMPLE_COLORS.ink : "#FFFFFF",
  backgroundColor: outlined ? "#FFFFFF" : undefined,
  // These render as links: keep the global a:focus / a:visited blue and outline off
  // (focus returns here when the monthly box closes). Keyboard focus still shows below.
  "&:focus, &:visited": {
    color: outlined ? TEMPLE_COLORS.ink : "#FFFFFF",
    outline: "none",
  },
  border: outlined ? "1.5px solid rgba(29, 29, 27, 0.35)" : "none",
  boxShadow: outlined ? "none" : "0px 8px 20px 0px rgba(240, 73, 81, 0.3)",
  transition: "box-shadow 0.25s ease, border-color 0.25s ease, color 0.25s ease, filter 0.25s ease",
  // Hover: the red glow the buttons already carry, brighter and wider.
  "&:hover": outlined
    ? {
        backgroundColor: "#FFFFFF",
        borderColor: TEMPLE_COLORS.red,
        color: TEMPLE_COLORS.red,
        textDecoration: "none",
        boxShadow: "0px 8px 22px 0px rgba(240, 73, 81, 0.35)",
      }
    : {
        // These render as links; keep the global a:hover blue/underline off.
        color: "#FFFFFF",
        textDecoration: "none",
        boxShadow: "0px 0px 0px 4px rgba(240, 73, 81, 0.18), 0px 10px 30px 0px rgba(240, 73, 81, 0.6)",
        filter: "brightness(1.06)",
      },
  "&:focus-visible": {
    outline: `2px solid ${TEMPLE_COLORS.ink}`,
    outlineOffset: "2px",
  },
}));

// Button link "#donate-monthly" (set in admin) opens the temple monthly donation box.
export const DONATE_MONTHLY_LINK = "#donate-monthly";
export const OPEN_MONTHLY_BOX_EVENT = "temple:open-monthly-box";

// In-page anchors ("#featured-temples") scroll; anything else is a route.
export const TempleButton = ({ link, color = TEMPLE_COLORS.red, children, ...props }) => {
  const linkProps = link === DONATE_MONTHLY_LINK
    ? {
        component: "a",
        href: link,
        onClick: (e) => {
          e.preventDefault();
          window.dispatchEvent(new Event(OPEN_MONTHLY_BOX_EVENT));
        },
      }
    : link?.startsWith("#")
    ? { component: "a", href: link }
    : link
      ? { component: Link, to: link }
      : {};
  return (
    <StyledTempleButton
      {...linkProps}
      {...props}
      sx={{
        ...(props.outlined ? {} : { backgroundColor: color, "&:hover": { backgroundColor: color } }),
        ...props.sx,
      }}
    >
      {children}
    </StyledTempleButton>
  );
};

// Mobile: a horizontally swipeable row of fixed-width cards with page dots.
export const SwipeRow = ({ children, itemWidth, gap = 16, px = 0 }) => {
  const rowRef = useRef(null);
  const [active, setActive] = useState(0);
  const items = Children.toArray(children);

  const handleScroll = () => {
    const row = rowRef.current;
    if (!row) return;
    setActive(Math.min(items.length - 1, Math.round(row.scrollLeft / (itemWidth + gap))));
  };

  const goTo = (index) => rowRef.current?.scrollTo({ left: index * (itemWidth + gap), behavior: "smooth" });

  return (
    <>
      <Box
        ref={rowRef}
        onScroll={handleScroll}
        sx={{
          display: "flex",
          gap: `${gap}px`,
          px: `${px}px`,
          overflowX: "auto",
          scrollSnapType: "x mandatory",
          scrollPaddingLeft: `${px}px`,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          WebkitOverflowScrolling: "touch",
        }}
      >
        {items.map((item, index) => (
          <Box key={index} sx={{ flex: `0 0 ${itemWidth}px`, scrollSnapAlign: "start" }}>
            {item}
          </Box>
        ))}
      </Box>
      {items.length > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "6px" }}>
          {items.map((_, index) => (
            <Box
              key={index}
              component="button"
              type="button"
              aria-label={`Show item ${index + 1}`}
              onClick={() => goTo(index)}
              sx={{
                p: 0,
                border: "none",
                cursor: "pointer",
                width: index === active ? 20 : 6,
                height: 6,
                borderRadius: "3px",
                background: index === active ? TEMPLE_COLORS.red : "#D9D9D9",
                transition: "width 0.2s ease, background-color 0.2s ease",
              }}
            />
          ))}
        </Box>
      )}
    </>
  );
};
